<template>
  <MainLayout class="mqtt-index">
    <div class="mqtt-page">
      <!-- Subscribe Section -->
      <Card :bordered="false" class="section-card">
        <p slot="title">
          <Icon type="ios-pulse" />
          MQTT 订阅
        </p>
        <div class="subscribe-row">
          <Input v-model="subTopic" placeholder="输入要订阅的 Topic，例如 test/#" class="topic-input" clearable>
            <span slot="prepend">Topic</span>
          </Input>
          <Button v-if="!subscribing" type="primary" @click="startSubscribe" :loading="connecting" icon="ios-link">
            订阅
          </Button>
          <Button v-else type="error" @click="stopSubscribe" icon="ios-close-circle-outline">
            取消订阅
          </Button>
        </div>
        <div class="status-row">
          <Tag v-if="subscribing" color="green">已订阅: {{ subTopic }}</Tag>
          <Tag v-else color="default">未订阅</Tag>
          <span class="msg-count">收到消息: {{ messages.length }} 条</span>
        </div>
      </Card>

      <!-- Messages Display -->
      <Card :bordered="false" class="section-card msg-card">
        <p slot="title">
          <Icon type="ios-list-box-outline" />
          订阅消息
          <Button size="small" @click="messages = []" style="float:right" icon="ios-trash">清空</Button>
        </p>
        <div class="msg-list" ref="msgList">
          <div v-if="messages.length === 0" class="empty-state">
            <Icon type="ios-chatbubbles-outline" size="48" color="#c5c8ce" />
            <p>暂无消息，请先订阅 Topic</p>
          </div>
          <div v-for="(msg, i) in messages" :key="i" class="msg-item">
            <div class="msg-header">
              <Tag color="blue">{{ msg.topic }}</Tag>
              <span class="msg-time">{{ msg.time }}</span>
            </div>
            <div class="msg-body">{{ msg.payload }}</div>
          </div>
        </div>
      </Card>

      <!-- Publish Section -->
      <Card :bordered="false" class="section-card">
        <p slot="title">
          <Icon type="ios-send-outline" />
          MQTT 发布
        </p>
        <div class="publish-row">
          <Input v-model="pubTopic" placeholder="输入要发布的 Topic" class="topic-input" clearable>
            <span slot="prepend">Topic</span>
          </Input>
        </div>
        <div class="publish-row">
          <Input v-model="pubPayload" type="textarea" :rows="3" placeholder="输入要发布的消息内容" class="payload-input" />
        </div>
        <div class="publish-row">
          <span class="qos-label">QoS:</span>
          <RadioGroup v-model="pubQos">
            <Radio :label="0">0 - 最多一次</Radio>
            <Radio :label="1">1 - 至少一次</Radio>
            <Radio :label="2">2 - 恰好一次</Radio>
          </RadioGroup>
        </div>
        <Button type="success" @click="doPublish" :loading="publishing" icon="ios-paper-plane" size="large">
          发布消息
        </Button>
      </Card>
    </div>
  </MainLayout>
</template>

<script>
import { API_URL } from '@/constants/const';
import MainLayout from '@/components/MainLayout.vue'
import axios from 'axios';
import { postJsError } from '@/util/util.js';

export default {
  name: 'MqttIndex',
  components: {
    MainLayout,    
  },
  data() {
    return {
      // Subscribe
      subTopic: '',
      subscribing: false,
      connecting: false,
      eventSource: null,
      messages: [],

      // Publish
      pubTopic: '',
      pubPayload: '',
      pubQos: 0,
      publishing: false,
    }
  },
  created() {
    this.init();
  },
  beforeDestroy() {
    this.stopSubscribe();
  },
  methods: {
    init() {
      
    },

    startSubscribe() {
        
      if (!this.subTopic.trim()) {
        this.$Message.warning('请输入 Topic');
        return;
      }
      this.connecting = true;

      // Build SSE URL with topic parameter
      const url = API_URL["mqttSubscribe"] + '?topic=' + encodeURIComponent(this.subTopic.trim());

      // Close any existing connection
      this.stopSubscribe();

      const es = new EventSource(url);
      this.eventSource = es;

      es.onopen = () => {
        this.connecting = false;
        this.subscribing = true;
        this.$Message.success('已订阅: ' + this.subTopic);
      };

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // The initial "connected" event is just a handshake, don't add to messages
          if (data.status === 'connected') {
            return;
          }
          this.messages.push({
            topic: data.topic,
            payload: data.payload,
            time: data.time,
          });
          // Auto-scroll to bottom
          this.$nextTick(() => {
            const el = this.$refs.msgList;
            if (el) {
              el.scrollTop = el.scrollHeight;
            }
          });
        } catch (e) {
          console.error('SSE parse error:', e);
        }
      };

      es.onerror = () => {
        this.connecting = false;
        // EventSource will auto-reconnect; if it stays closed, mark as unsubscribed
        if (es.readyState === EventSource.CLOSED) {
          this.subscribing = false;
          this.$Message.error('SSE 连接已关闭');
        }
      };
    },

    stopSubscribe() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      this.subscribing = false;
      this.connecting = false;
    },

    async doPublish() {
      if (!this.pubTopic.trim()) {
        this.$Message.warning('请输入 Topic');
        return;
      }
      if (!this.pubPayload.trim()) {
        this.$Message.warning('请输入消息内容');
        return;
      }

      this.publishing = true;
      try {
        const res = await axios.post(API_URL["mqttPublish"], {
          topic: this.pubTopic.trim(),
          payload: this.pubPayload,
          qos: this.pubQos,
        }, {
          headers: { 'Content-Type': 'application/json' }
        });

        if (res && res.data && res.data.code == 200) {
          this.$Message.success('消息已发布到: ' + this.pubTopic);
        } else if (res && res.data && res.data.code == 3001) {
          this.$router.push('/login')
        } else if (res && res.data && res.data.msg) {
          this.$Message.error(res.data.msg);
        }
      } catch (err) {
        postJsError(err);
        this.$Message.error('发布失败，请检查服务状态');
      } finally {
        this.publishing = false;
      }
    },
  }
}
</script>

<style scoped>
.mqtt-page {
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;
}

.section-card {
  margin-bottom: 20px;
}

.subscribe-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.subscribe-row .topic-input {
  flex: 1;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  color: #808695;
}

.msg-count {
  color: #2d8cf0;
}

.msg-list {
  max-height: 400px;
  overflow-y: auto;
  background: #f8f8f9;
  border-radius: 6px;
  padding: 12px;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
  color: #c5c8ce;
}

.empty-state p {
  margin-top: 12px;
  font-size: 14px;
}

.msg-item {
  background: #fff;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 8px;
  border-left: 3px solid #2d8cf0;
}

.msg-item:last-child {
  margin-bottom: 0;
}

.msg-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.msg-time {
  font-size: 12px;
  color: #808695;
}

.msg-body {
  font-family: 'Courier New', Consolas, monospace;
  font-size: 13px;
  word-break: break-all;
  white-space: pre-wrap;
  color: #333;
  line-height: 1.5;
}

.publish-row {
  margin-bottom: 14px;
}

.publish-row .topic-input {
  width: 100%;
}

.payload-input {
  width: 100%;
}

.qos-label {
  font-weight: 600;
  margin-right: 12px;
  color: #515a6e;
}
</style>
